/**
 * PPA module — centralized data-testid registry.
 *
 * Convention: `ppa_{page}_{element}` (static) or helpers that append stable
 * config ids (`kpi_id`, `workflow_id`, `filter_id`). Add new pages as sections
 * below; keep shared utilities at the top.
 */

// =============================================================================
// Shared utilities
// =============================================================================

/** Lowercase and replace non-alphanumeric runs with underscores for stable test id segments. */
export const slugifyTestIdSegment = (value: string): string =>
    value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');

// =============================================================================
// PPA Scope (cross-page — home ribbon, modals, other routes)
// =============================================================================

export const PPA_SCOPE_TEST_ID_PREFIX = 'ppa_scope';

export const PPA_SCOPE_RIBBON = 'ppa_scope_ribbon';
export const PPA_SCOPE_EDIT_BTN = 'ppa_scope_edit_btn';
export const PPA_SCOPE_MODAL = 'ppa_scope_modal';
export const PPA_SCOPE_MODAL_CANCEL_BTN = 'ppa_scope_modal_cancel_btn';
export const PPA_SCOPE_MODAL_APPLY_BTN = 'ppa_scope_modal_apply_btn';

export const ppaScopeFilterGroupTestId = (
    filterId: string,
    prefix = PPA_SCOPE_TEST_ID_PREFIX,
): string => `${prefix}_filter_${filterId}`;

export const ppaScopeFilterOptionTestId = (
    filterId: string,
    option: string,
    prefix = PPA_SCOPE_TEST_ID_PREFIX,
): string => `${prefix}_option_${filterId}_${slugifyTestIdSegment(option)}`;

export const ppaScopeRibbonValueTestId = (
    filterId: string,
    prefix = PPA_SCOPE_TEST_ID_PREFIX,
): string => `${prefix}_value_${filterId}`;

export const ppaScopeModalTestId = (prefix = PPA_SCOPE_TEST_ID_PREFIX): string =>
    `${prefix}_modal`;

export const ppaScopeModalCancelBtnTestId = (prefix = PPA_SCOPE_TEST_ID_PREFIX): string =>
    `${prefix}_modal_cancel_btn`;

export const ppaScopeModalApplyBtnTestId = (prefix = PPA_SCOPE_TEST_ID_PREFIX): string =>
    `${prefix}_modal_apply_btn`;

// =============================================================================
// Home page / Dashboard (`/ppa/home`)
// =============================================================================

export const PPA_HOME_KPI_TEST_ID_PREFIX = 'ppa_home';

// --- Static section ids ---
export const PPA_HOME_PAGE = 'ppa_home_page';
export const PPA_HOME_LOADING = 'ppa_home_loading';
export const PPA_HOME_HERO = 'ppa_home_hero';
export const PPA_HOME_KPI_SECTION = 'ppa_home_kpi_section';
export const PPA_HOME_KPI_SKELETON = 'ppa_home_kpi_skeleton';
export const PPA_HOME_ALERTS = 'ppa_home_alerts';
export const PPA_HOME_ACTIVITY = 'ppa_home_activity';
export const PPA_HOME_WORKFLOWS_SECTION = 'ppa_home_workflows_section';
export const PPA_HOME_WORKFLOWS_GRID = 'ppa_home_workflows_grid';
export const PPA_HOME_WORKFLOWS_LOADING = 'ppa_home_workflows_loading';
export const PPA_HOME_FOOTER_PROMPT = 'ppa_home_footer_prompt';

// --- Dynamic ids (keyed by config: kpi_id, workflow_id, tab key) ---
export const ppaKpiCardTestId = (kpiId: string, prefix = PPA_HOME_KPI_TEST_ID_PREFIX): string =>
    `${prefix}_kpi_card_${kpiId}`;

export const ppaKpiCardValueTestId = (kpiId: string, prefix = PPA_HOME_KPI_TEST_ID_PREFIX): string =>
    `${prefix}_kpi_value_${kpiId}`;

export const ppaKpiCardTrendTestId = (kpiId: string, prefix = PPA_HOME_KPI_TEST_ID_PREFIX): string =>
    `${prefix}_kpi_trend_${kpiId}`;

export const ppaWorkflowCardTestId = (workflowId: string): string =>
    `ppa_home_workflow_card_${workflowId}`;

export const ppaWorkflowTitleTestId = (workflowId: string): string =>
    `ppa_home_workflow_title_${workflowId}`;

export const ppaWorkflowIconTestId = (workflowId: string): string =>
    `ppa_home_workflow_icon_${workflowId}`;

export const ppaWorkflowPointTestId = (workflowId: string, pointIndex: number): string =>
    `ppa_home_workflow_point_${workflowId}_${pointIndex}`;

export const ppaWorkflowBtnTestId = (workflowId: string): string =>
    `ppa_home_workflow_btn_${workflowId}`;

export const ppaActivityTabTestId = (tabKey: string): string =>
    `ppa_home_activity_tab_${tabKey}`;

// =============================================================================
// Performance Summary / Opportunity Assessment (`/ppa/opportunity-assessment`)
// =============================================================================

export const PPA_PERF_SUMMARY_TEST_ID_PREFIX = 'ppa_perf_summary';

export const PPA_PERF_SUMMARY_PAGE = 'ppa_perf_summary_page';
export const PPA_PERF_SUMMARY_LOADING = 'ppa_perf_summary_loading';
export const PPA_PERF_SUMMARY_CONTENT = 'ppa_perf_summary_content';
export const PPA_PERF_SUMMARY_BACK_BTN = 'ppa_perf_summary_back_btn';
export const PPA_PERF_SUMMARY_TITLE = 'ppa_perf_summary_title';
export const PPA_PERF_SUMMARY_INFO_BTN = 'ppa_perf_summary_info_btn';
export const PPA_PERF_SUMMARY_EXPORT_BTN = 'ppa_perf_summary_export_btn';
export const PPA_PERF_SUMMARY_KPI_SECTION = 'ppa_perf_summary_kpi_section';
export const PPA_PERF_SUMMARY_OVERALL_SECTION = 'ppa_perf_summary_overall_section';
export const PPA_PERF_SUMMARY_LENS_SECTION = 'ppa_perf_summary_lens_section';
export const PPA_PERF_SUMMARY_SPLIT_RESIZER = 'ppa_perf_summary_split_resizer';
export const PPA_PERF_SUMMARY_EXPORT_MODAL = 'ppa_perf_summary_export_modal';
export const PPA_PERF_SUMMARY_EXPORT_CANCEL_BTN = 'ppa_perf_summary_export_cancel_btn';
export const PPA_PERF_SUMMARY_EXPORT_CONFIRM_BTN = 'ppa_perf_summary_export_confirm_btn';

export const ppaPerfSummaryExportOptionTestId = (option: string): string =>
    `ppa_perf_summary_export_option_${option}`;

export const ppaPerfSummarySectionTabTestId = (
    section: 'overall' | 'lens',
    tabKey: string,
): string => `ppa_perf_summary_${section}_tab_${slugifyTestIdSegment(tabKey)}`;

export type PerfSummarySection = 'overall' | 'lens';

export const ppaPerfSummaryDimensionBtnTestId = (section: PerfSummarySection): string =>
    `ppa_perf_summary_${section}_dimension_btn`;

export const ppaPerfSummaryKpiBtnTestId = (section: PerfSummarySection): string =>
    `ppa_perf_summary_${section}_kpi_btn`;

export const ppaPerfSummaryDimensionSearchTestId = (section: PerfSummarySection): string =>
    `ppa_perf_summary_${section}_dimension_search`;

export const ppaPerfSummaryDimensionOptionTestId = (
    section: PerfSummarySection,
    dimensionId: string,
): string => `ppa_perf_summary_${section}_dimension_option_${slugifyTestIdSegment(dimensionId)}`;

export const ppaPerfSummaryKpiSelectAllTestId = (section: PerfSummarySection): string =>
    `ppa_perf_summary_${section}_kpi_select_all`;

export const ppaPerfSummaryKpiOptionTestId = (section: PerfSummarySection, kpiId: string): string =>
    `ppa_perf_summary_${section}_kpi_option_${slugifyTestIdSegment(kpiId)}`;

export const ppaPerfSummaryKpiResetBtnTestId = (section: PerfSummarySection): string =>
    `ppa_perf_summary_${section}_kpi_reset_btn`;

export const ppaPerfSummaryKpiApplyBtnTestId = (section: PerfSummarySection): string =>
    `ppa_perf_summary_${section}_kpi_apply_btn`;

export const ppaPerfSummarySortAscTestId = (section: PerfSummarySection, columnId: string): string =>
    `ppa_perf_summary_${section}_sort_asc_${slugifyTestIdSegment(columnId)}`;

export const ppaPerfSummarySortDescTestId = (section: PerfSummarySection, columnId: string): string =>
    `ppa_perf_summary_${section}_sort_desc_${slugifyTestIdSegment(columnId)}`;

export const ppaPerfSummaryLensKpiInputTestId = (kpiId: string): string =>
    `ppa_perf_summary_lens_kpi_input_${slugifyTestIdSegment(kpiId)}`;

export const ppaPerfSummaryLensKpiStepUpTestId = (kpiId: string): string =>
    `ppa_perf_summary_lens_kpi_step_up_${slugifyTestIdSegment(kpiId)}`;

export const ppaPerfSummaryLensKpiStepDownTestId = (kpiId: string): string =>
    `ppa_perf_summary_lens_kpi_step_down_${slugifyTestIdSegment(kpiId)}`;

// =============================================================================
// Shared axis / KPI axis configuration (Whitespace + Pack Architecture)
// =============================================================================

export const PPA_AXIS_CONFIG_PANEL = 'ppa_axis_config_panel';
export const PPA_AXIS_CONFIG_APPLY_BTN = 'ppa_axis_config_apply_btn';
export const PPA_AXIS_CONFIG_MODAL = 'ppa_axis_config_modal';
export const PPA_AXIS_CONFIG_MODAL_RESET_BTN = 'ppa_axis_config_modal_reset_btn';
export const PPA_AXIS_CONFIG_MODAL_CANCEL_BTN = 'ppa_axis_config_modal_cancel_btn';
export const PPA_AXIS_CONFIG_MODAL_APPLY_BTN = 'ppa_axis_config_modal_apply_btn';
export const PPA_AXIS_CONFIG_ORDER_ASC = 'ppa_axis_config_order_asc';
export const PPA_AXIS_CONFIG_ORDER_DESC = 'ppa_axis_config_order_desc';
export const PPA_AXIS_CONFIG_SHOW_TOP_INPUT = 'ppa_axis_config_show_top_input';
export const PPA_AXIS_CONFIG_GROUP_REST_CHECKBOX = 'ppa_axis_config_group_rest_checkbox';

export const ppaAxisConfigOptionTestId = (value: string): string =>
    `ppa_axis_config_option_${slugifyTestIdSegment(value)}`;

export const ppaAxisConfigSortByTestId = (sortById: string): string =>
    `ppa_axis_config_sort_by_${slugifyTestIdSegment(sortById)}`;

export const PPA_KPI_AXIS_CONFIG_MODAL = 'ppa_kpi_axis_config_modal';
export const PPA_KPI_AXIS_BINNING_UNIFORM = 'ppa_kpi_axis_binning_uniform';
export const PPA_KPI_AXIS_BINNING_CUSTOM = 'ppa_kpi_axis_binning_custom';
export const PPA_KPI_AXIS_RANGE_MIN = 'ppa_kpi_axis_range_min';
export const PPA_KPI_AXIS_RANGE_MAX = 'ppa_kpi_axis_range_max';
export const PPA_KPI_AXIS_BIN_COUNT = 'ppa_kpi_axis_bin_count';
export const PPA_KPI_AXIS_BIN_SIZE = 'ppa_kpi_axis_bin_size';

// =============================================================================
// Whitespace Analysis (`/ppa/whitespace-analysis`)
// =============================================================================

export const PPA_WHITESPACE_PAGE = 'ppa_whitespace_page';
export const PPA_WHITESPACE_LOADING = 'ppa_whitespace_loading';
export const PPA_WHITESPACE_CONTENT = 'ppa_whitespace_content';
export const PPA_WHITESPACE_BACK_BTN = 'ppa_whitespace_back_btn';
export const PPA_WHITESPACE_TITLE = 'ppa_whitespace_title';
export const PPA_WHITESPACE_INFO_BTN = 'ppa_whitespace_info_btn';
export const PPA_WHITESPACE_EXPORT_BTN = 'ppa_whitespace_export_btn';
export const PPA_WHITESPACE_TOOLBAR = 'ppa_whitespace_toolbar';
export const PPA_WHITESPACE_AXIS_X = 'ppa_whitespace_axis_x';
export const PPA_WHITESPACE_AXIS_X_SETTINGS = 'ppa_whitespace_axis_x_settings';
export const PPA_WHITESPACE_AXIS_Y = 'ppa_whitespace_axis_y';
export const PPA_WHITESPACE_AXIS_Y_SETTINGS = 'ppa_whitespace_axis_y_settings';
export const PPA_WHITESPACE_CROSS_SECTION_BTN = 'ppa_whitespace_cross_section_btn';
export const PPA_WHITESPACE_SHOW_TOGGLE = 'ppa_whitespace_show_toggle';
export const PPA_WHITESPACE_GRID = 'ppa_whitespace_grid';
export const PPA_WHITESPACE_GRID_LOADING = 'ppa_whitespace_grid_loading';
export const PPA_WHITESPACE_GRID_EMPTY = 'ppa_whitespace_grid_empty';
export const PPA_WHITESPACE_NO_DATA = 'ppa_whitespace_no_data';
export const PPA_WHITESPACE_EXPORT_MODAL = 'ppa_whitespace_export_modal';
export const PPA_WHITESPACE_EXPORT_CANCEL_BTN = 'ppa_whitespace_export_cancel_btn';
export const PPA_WHITESPACE_EXPORT_CONFIRM_BTN = 'ppa_whitespace_export_confirm_btn';

export const ppaWhitespaceCrossSectionOptionTestId = (option: string): string =>
    `ppa_whitespace_cross_section_option_${slugifyTestIdSegment(option)}`;

export const ppaWhitespaceExportOptionTestId = (
    group: 'scope' | 'format',
    value: string,
): string => `ppa_whitespace_export_${group}_${slugifyTestIdSegment(value)}`;

// =============================================================================
// Price Pack Architecture (`/ppa/pack-architecture`)
// =============================================================================

export const PPA_PACK_ARCH_PAGE = 'ppa_pack_arch_page';
export const PPA_PACK_ARCH_CONTENT = 'ppa_pack_arch_content';
export const PPA_PACK_ARCH_BACK_BTN = 'ppa_pack_arch_back_btn';
export const PPA_PACK_ARCH_TITLE = 'ppa_pack_arch_title';
export const PPA_PACK_ARCH_INFO_BTN = 'ppa_pack_arch_info_btn';
export const PPA_PACK_ARCH_EXPORT_BTN = 'ppa_pack_arch_export_btn';
export const PPA_PACK_ARCH_TOOLBAR = 'ppa_pack_arch_toolbar';
export const PPA_PACK_ARCH_AXIS_X = 'ppa_pack_arch_axis_x';
export const PPA_PACK_ARCH_AXIS_X_SETTINGS = 'ppa_pack_arch_axis_x_settings';
export const PPA_PACK_ARCH_AXIS_Y = 'ppa_pack_arch_axis_y';
export const PPA_PACK_ARCH_BUBBLE_REPRESENTS = 'ppa_pack_arch_bubble_represents';
export const PPA_PACK_ARCH_BUBBLE_SIZE = 'ppa_pack_arch_bubble_size';
export const PPA_PACK_ARCH_HEATMAP_TOGGLE = 'ppa_pack_arch_heatmap_toggle';
export const PPA_PACK_ARCH_CHART = 'ppa_pack_arch_chart';
export const PPA_PACK_ARCH_CHART_ERROR = 'ppa_pack_arch_chart_error';
export const PPA_PACK_ARCH_CHART_EMPTY = 'ppa_pack_arch_chart_empty';
export const PPA_PACK_ARCH_CHART_LOADING = 'ppa_pack_arch_chart_loading';
export const PPA_PACK_ARCH_LEGEND_EXPAND_BTN = 'ppa_pack_arch_legend_expand_btn';
export const PPA_PACK_ARCH_TABLE = 'ppa_pack_arch_table';
export const PPA_PACK_ARCH_TABLE_SEARCH = 'ppa_pack_arch_table_search';
export const PPA_PACK_ARCH_TABLE_SEARCH_SUBMIT = 'ppa_pack_arch_table_search_submit';
export const PPA_PACK_ARCH_TABLE_EMPTY = 'ppa_pack_arch_table_empty';
export const PPA_PACK_ARCH_NO_DATA = 'ppa_pack_arch_no_data';
export const PPA_PACK_ARCH_EXPORT_MODAL = 'ppa_pack_arch_export_modal';
export const PPA_PACK_ARCH_EXPORT_CANCEL_BTN = 'ppa_pack_arch_export_cancel_btn';
export const PPA_PACK_ARCH_EXPORT_CONFIRM_BTN = 'ppa_pack_arch_export_confirm_btn';

export const ppaPackArchTableKpiTestId = (kpiId: string): string =>
    `ppa_pack_arch_table_kpi_${slugifyTestIdSegment(kpiId)}`;

export const ppaPackArchLegendChipTestId = (segmentId: string): string =>
    `ppa_pack_arch_legend_chip_${slugifyTestIdSegment(segmentId)}`;

export const ppaPackArchExportOptionTestId = (
    group: 'scope' | 'format',
    value: string,
): string => `ppa_pack_arch_export_${group}_${slugifyTestIdSegment(value)}`;

// =============================================================================
// Price Simulator v3 (`/ppa/price-simulator-v3`)
// =============================================================================
// TODO: add static + dynamic ids when instrumenting this page
